import { PubSubService } from '../_shared/pubSubServiceInterface';
import {
  getAuthToken,
  getAuthHeaders,
} from '../../../../../extensions/radiology-report/src/utils/dicomUtils';
import { RIS_API_BASE_URL } from '../../../../app/src/custom/config/api.config';

export interface Template {
  id: string;
  name: string;
  modality: 'MR' | 'CT' | 'XR' | 'US';
  findings: string;
  impression: string;
  isDocumentImport?: boolean; // Flag to indicate if template was imported from a document
  anatomy_category?: string; // Body part category for the template
  anatomy_id?: number; // Body part ID for the template
  facility_id?: number | number[]; // Facility ID(s) for the template (used by ris-super-admin, array for multiple facilities)
  access_level?: 'Private' | 'Public' | 'Department'; // Access level for the template
  status?: 'Active' | 'Inactive'; // Template status
  version?: number; // Template version (this row / revision)
  /** Master-templates list: catalog max version; next save is typically latest_version + 1 */
  latest_version?: number;
  is_deleted?: boolean; // Flag to indicate if template is deleted
  createdAt: string;
  updatedAt: string;
  /** Table: view / procedure short label (optional; from API) */
  viewLabel?: string;
  /** Table: diagnosis line (optional; from API) */
  diagnosisLabel?: string;
  /** Tenant POST/PATCH: optional short label after fixed prefix (`template_description`). */
  template_description?: string;
  /** GET list: structured title without description suffix (`template_name_without_description`). */
  templateNameWithoutDescription?: string;
  /** GET list: duplicate-instance label from API (`copy_version`), e.g. shown as `(copy5)`. */
  copyVersion?: string | number;
  /** Table: who last updated the template (optional; from API) */
  updatedByName?: string;
  /** List: Custom (tenant) vs System — from API `template_type` / `type` */
  listType?: 'Custom' | 'System';
  /** Tenant list: newer master version exists for imported system template */
  updateAvailable?: boolean;
  /** Optional rich-text section (compare modal; API may send template_content_clinical_technique, etc.) */
  clinicalTechnique?: string;
}

const STORAGE_KEY = 'ohif-templates';

/** RIS JSON envelopes may use `error` or `message`, and sometimes HTTP 200 with `success: false`. */
type RisEnvelope = {
  success?: boolean;
  error?: string;
  message?: string;
  data?: any;
  pagination?: { totalItems?: number; currentPage?: number; totalPages?: number };
  total?: number;
  page?: number;
  totalPages?: number;
};

/** Row from GET /ris/api/templates/master-catalog */
export interface MasterCatalogItem {
  id: number;
  template_name: string;
  version: number;
  imported: boolean;
}

/** POST /ris/api/templates/master-action */
export type TemplateMasterActionType = 'UPDATE' | 'KEEP_CURRENT';

/** POST `/ris/api/templates/status` */
export type TemplateRisStatus = 'ACTIVE' | 'INACTIVE';

export interface TemplateMasterActionResultItem {
  id: number;
  status: string;
  action?: string;
  message?: string;
}

function parseRisJsonBody(text: string): RisEnvelope {
  const t = text.trim();
  if (!t) {
    return {};
  }
  try {
    return JSON.parse(t) as RisEnvelope;
  } catch {
    return {};
  }
}

/** Fail on non-OK status or `{ success: false }` — surfaces `error` string to the UI via thrown Error. */
function assertRisSuccess(response: Response, body: RisEnvelope): void {
  if (!response.ok) {
    throw new Error(body.error || body.message || `API request failed: ${response.status}`);
  }
  if (body.success === false) {
    throw new Error(body.error || body.message || 'Request failed');
  }
}

// Helper to get API base URL and endpoints
const getApiConfig = () => {
  // Dynamically import API config to avoid circular dependencies
  // We'll use the same base URL pattern as the API config
  // const RIS_API_BASE_URL = 'http://20.244.40.31:5000';

  return {
    TEMPLATES: {
      GET: `${RIS_API_BASE_URL}/ris/api/templates`,
      GET_BY_ID: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/templates/${id}`,
      /** Facility template vs published master row (tenant “Update available”) */
      WITH_MASTER: (id: string | number) =>
        `${RIS_API_BASE_URL}/ris/api/templates/${id}/with-master`,
      CREATE: `${RIS_API_BASE_URL}/ris/api/templates`,
      UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/templates/${id}`,
      /** Tenant: activate/deactivate (`ACTIVE` / `INACTIVE`) — single `template_id` or bulk `template_ids` */
      STATUS: `${RIS_API_BASE_URL}/ris/api/templates/status`,
      MASTER_CATALOG: `${RIS_API_BASE_URL}/ris/api/templates/master-catalog`,
      /** Tenant bulk/single: apply master update or dismiss “update available” */
      MASTER_ACTION: `${RIS_API_BASE_URL}/ris/api/templates/master-action`,
      IMPORT_MASTER_CATALOG: `${RIS_API_BASE_URL}/ris/api/templates`,
    },
    MASTER_TEMPLATES: {
      UPDATE: (id: string | number) => `${RIS_API_BASE_URL}/ris/api/master-templates/${id}`,
    },
  };
};

class TemplateService extends PubSubService {
  public static REGISTRATION = {
    name: 'templateService',
    create: ({ configuration = {} }) => {
      return new TemplateService();
    },
  };

  public static EVENTS = {
    TEMPLATE_CREATED: 'event::templateService:templateCreated',
    TEMPLATE_UPDATED: 'event::templateService:templateUpdated',
    TEMPLATE_DELETED: 'event::templateService:templateDeleted',
  };

  constructor() {
    super(TemplateService.EVENTS);
  }

  /**
   * RIS `is_deleted`: when true, row is inactive (toggle off). false/0/null/undefined = active (toggle on).
   * Avoids `Boolean("false") === true` if a layer ever stringifies flags.
   */
  /** List column: Custom vs System (tenant templates). */
  private _mapTemplateListType(item: Record<string, unknown>): 'Custom' | 'System' {
    const raw = String(item.template_type ?? item.type ?? item.templateType ?? '')
      .trim()
      .toLowerCase();
    if (raw === 'system') {
      return 'System';
    }
    if (raw === 'custom') {
      return 'Custom';
    }
    if (item.is_system_template === true || item.is_system === true) {
      return 'System';
    }
    return 'Custom';
  }

  private _mapApiIsDeleted(value: unknown): boolean {
    if (value === true || value === 1) {
      return true;
    }
    if (value === false || value === 0) {
      return false;
    }
    if (typeof value === 'string') {
      const s = value.trim().toLowerCase();
      if (s === '1' || s === 'true' || s === 'yes') {
        return true;
      }
      if (s === '0' || s === 'false' || s === 'no' || s === '') {
        return false;
      }
    }
    return false;
  }

  /** Map a single RIS template row (list item or `with-master` payload) to `Template`. */
  private _mapApiTemplateRow(item: Record<string, any>): Template {
    const row = item;
    const ctRaw =
      row.template_content_clinical_technique ??
      row.template_content_technique ??
      row.clinical_technique;
    const clinicalTechnique =
      typeof ctRaw === 'string' && ctRaw.trim() ? ctRaw : undefined;

    return {
      id: row.id?.toString() || this._generateId(),
      name: row.template_name || row.name,
      modality: (row.Modality?.name || row.modality) as 'MR' | 'CT' | 'XR' | 'US',
      findings: row.template_content_findings || row.findings || '',
      impression: row.template_content_impression || row.impression || '',
      isDocumentImport: row.is_imported || false,
      anatomy_category: row.anatomy_category || row.AnatomyPart?.name || '',
      anatomy_id: row.anatomy_id,
      facility_id: row.facility_id,
      access_level: row.access_level || 'Private',
      status: row.status || (this._mapApiIsDeleted(row.is_deleted) ? 'Inactive' : 'Active'),
      version: row.version ?? 1,
      latest_version:
        typeof row.latest_version === 'number'
          ? row.latest_version
          : typeof row.version === 'number'
            ? row.version
            : undefined,
      is_deleted: this._mapApiIsDeleted(row.is_deleted),
      createdAt: row.createdAt || row.created_at || new Date().toISOString(),
      updatedAt: row.updatedAt || row.updated_at || new Date().toISOString(),
      viewLabel: row.view ?? row.template_view ?? row.procedure_view ?? row.study_view ?? '',
      diagnosisLabel: row.diagnosis ?? row.clinical_diagnosis ?? row.diagnosis_short ?? '',
      template_description:
        row.template_description != null ? String(row.template_description) : undefined,
      templateNameWithoutDescription:
        row.template_name_without_description != null
          ? String(row.template_name_without_description)
          : undefined,
      copyVersion:
        row.copy_version != null && row.copy_version !== ''
          ? row.copy_version
          : undefined,
      updatedByName:
        row.LastModifier?.full_name ??
        row.updated_by_name ??
        row.last_modified_by ??
        row.modifier_name ??
        row.updated_by?.name ??
        row.User?.full_name ??
        row.User?.name ??
        '',
      listType: this._mapTemplateListType(row as Record<string, unknown>),
      updateAvailable: row.update_available === true,
      clinicalTechnique,
    };
  }

  /**
   * Get all templates from localStorage
   */
  public getAllTemplates(): Template[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading templates from localStorage:', error);
      return [];
    }
  }

  /**
   * Get templates by modality (from localStorage cache)
   */
  public getTemplatesByModality(modality: string): Template[] {
    const templates = this.getAllTemplates();
    return templates.filter(t => t.modality === modality);
  }

  /**
   * Fetch templates by modality from API (tenant `GET /ris/api/templates`).
   * Pass `search` for server-side filtering (same query as Templates module).
   */
  public async fetchTemplatesByModality(
    modality: string,
    options?: { search?: string }
  ): Promise<Template[]> {
    const q = options?.search?.trim();
    const result = await this.fetchTemplatesFromApi({
      modality,
      limit: 100,
      page: 1,
      status: 'ACTIVE',
      ...(q ? { search: q } : {}),
    });
    return result.templates;
  }

  /**
   * Get template by ID
   */
  public getTemplateById(id: string): Template | null {
    const templates = this.getAllTemplates();
    return templates.find(t => t.id === id) || null;
  }

  /**
   * Create a new template
   * @param options.useMasterTemplates — POST `/ris/api/master-templates` (ris-super-admin global catalog); requires `modalityId`.
   */
  public async createTemplate(
    template: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>,
    options?: { useMasterTemplates?: boolean; modalityId?: number }
  ): Promise<Template> {
    const apiConfig = getApiConfig();

    const useMaster =
      options?.useMasterTemplates === true &&
      options.modalityId != null &&
      !Number.isNaN(options.modalityId);

    if (useMaster) {
      const explicitName = (template.name || '').trim();
      if (template.anatomy_id == null && !explicitName) {
        throw new Error('Body part is required');
      }

      const requestBody: Record<string, unknown> = {
        modality_id: options!.modalityId as number,
        view: (template.viewLabel ?? '').trim(),
        diagnosis: (template.diagnosisLabel ?? '').trim(),
        is_imported: template.isDocumentImport || false,
        template_content_findings: template.findings || '',
        template_content_impression: template.impression || '',
      };
      if (template.anatomy_id != null) {
        requestBody.anatomy_id = template.anatomy_id;
      }
      if (explicitName) {
        requestBody.template_name = explicitName;
      }
      if (template.is_deleted === true) {
        requestBody.is_deleted = true;
      }

      const response = await fetch(`${RIS_API_BASE_URL}/ris/api/master-templates`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      const apiResponse = parseRisJsonBody(await response.text());
      assertRisSuccess(response, apiResponse);
      const row = apiResponse.data as Record<string, any> | undefined;

      const newTemplate: Template = {
        id: row?.id?.toString() || this._generateId(),
        name: row?.template_name || template.name,
        modality: (row?.Modality?.name || template.modality) as 'MR' | 'CT' | 'XR' | 'US',
        findings: row?.template_content_findings || template.findings || '',
        impression: row?.template_content_impression || template.impression || '',
        isDocumentImport: row?.is_imported ?? template.isDocumentImport,
        anatomy_category: row?.AnatomyPart?.name || template.anatomy_category,
        anatomy_id: row?.anatomy_id ?? template.anatomy_id,
        access_level: template.access_level || 'Private',
        status: row?.status || (row?.is_deleted === true ? 'Inactive' : 'Active'),
        version: row?.version ?? 1,
        is_deleted: Boolean(row?.is_deleted),
        createdAt: row?.createdAt || new Date().toISOString(),
        updatedAt: row?.updatedAt || new Date().toISOString(),
        viewLabel: row?.view ?? template.viewLabel,
        diagnosisLabel: row?.diagnosis ?? template.diagnosisLabel,
        updatedByName:
          row?.LastModifier?.full_name ?? row?.Creator?.full_name ?? template.updatedByName,
      };

      this._broadcastEvent(TemplateService.EVENTS.TEMPLATE_CREATED, newTemplate);
      return newTemplate;
    }

    /** Tenant templates: POST `/ris/api/templates` with modality_id / anatomy_id / view / diagnosis (no template_name). */
    const tenantCatalogShape =
      options?.modalityId != null && !Number.isNaN(options.modalityId as number);

    if (tenantCatalogShape) {
      if (template.anatomy_id == null) {
        throw new Error('Body part is required');
      }

      const requestBody: Record<string, unknown> = {
        modality_id: options!.modalityId as number,
        anatomy_id: template.anatomy_id,
        view: (template.viewLabel ?? '').trim(),
        diagnosis: (template.diagnosisLabel ?? '').trim(),
        template_description: (template.template_description ?? '').trim(),
        is_imported: Boolean(template.isDocumentImport),
        template_content_findings: template.findings || '',
      };

      const impressionTrim = (template.impression || '').trim();
      if (!(template.isDocumentImport && !impressionTrim)) {
        requestBody.template_content_impression = template.impression || '';
      }

      if (template.access_level) {
        requestBody.access_level = template.access_level;
      }
      if (template.status) {
        requestBody.status = template.status;
      }
      if (template.facility_id) {
        requestBody.facility_id = template.facility_id;
      }
      if (template.is_deleted === true) {
        requestBody.is_deleted = true;
      }

      const response = await fetch(apiConfig.TEMPLATES.CREATE, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      const apiResponse = parseRisJsonBody(await response.text());
      assertRisSuccess(response, apiResponse);

      const newTemplate: Template = {
        id: apiResponse.data?.id?.toString() || this._generateId(),
        name: apiResponse.data?.template_name || template.name,
        modality: (apiResponse.data?.Modality?.name ||
          apiResponse.data?.modality ||
          template.modality) as 'MR' | 'CT' | 'XR' | 'US',
        findings: apiResponse.data?.template_content_findings || template.findings || '',
        impression: apiResponse.data?.template_content_impression || template.impression || '',
        isDocumentImport: apiResponse.data?.is_imported ?? template.isDocumentImport,
        anatomy_category: apiResponse.data?.anatomy_category || template.anatomy_category,
        anatomy_id: apiResponse.data?.anatomy_id ?? template.anatomy_id,
        facility_id: apiResponse.data?.facility_id || template.facility_id,
        access_level: apiResponse.data?.access_level || template.access_level || 'Private',
        status: apiResponse.data?.status || template.status || 'Active',
        version: apiResponse.data?.version || 1,
        createdAt: apiResponse.data?.createdAt || new Date().toISOString(),
        updatedAt: apiResponse.data?.updatedAt || new Date().toISOString(),
        viewLabel: apiResponse.data?.view ?? template.viewLabel,
        diagnosisLabel: apiResponse.data?.diagnosis ?? template.diagnosisLabel,
      };

      this._broadcastEvent(TemplateService.EVENTS.TEMPLATE_CREATED, newTemplate);
      return newTemplate;
    }

    // Legacy create (e.g. JSON import): template_name + modality string
    const requestBody: any = {
      template_name: template.name,
      modality: template.modality,
      anatomy_category: template.anatomy_category || '',
      is_imported: template.isDocumentImport || false, // Set to true if template was imported from a document
      template_content_findings: template.findings || '', // Content goes here for document imports
      template_content_impression: template.impression || '',
      access_level: template.access_level || 'Private',
      status: template.status || 'Active', // Default to Active
    };

    // Add anatomy_id if provided
    if (template.anatomy_id) {
      requestBody.anatomy_id = template.anatomy_id;
    }

    // Add facility_id if provided (for ris-super-admin)
    // This is the facility_id(s) selected from the dropdown, NOT the user's own facility_id
    // Can be a single number or an array of numbers
    if (template.facility_id) {
      requestBody.facility_id = template.facility_id;
    }

    // Save to API (no localStorage fallback for creating)
    const response = await fetch(apiConfig.TEMPLATES.CREATE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody),
    });

    const apiResponse = parseRisJsonBody(await response.text());
    assertRisSuccess(response, apiResponse);

    // Create template object from API response
    const newTemplate: Template = {
      id: apiResponse.data?.id?.toString() || this._generateId(),
      name: apiResponse.data?.template_name || template.name,
      // Extract modality from Modality.name if Modality object exists, otherwise fallback to template.modality
      modality: (apiResponse.data?.Modality?.name ||
        apiResponse.data?.modality ||
        template.modality) as 'MR' | 'CT' | 'XR' | 'US',
      findings: apiResponse.data?.template_content_findings || template.findings || '',
      impression: apiResponse.data?.template_content_impression || template.impression || '',
      isDocumentImport: apiResponse.data?.is_imported || template.isDocumentImport,
      anatomy_category: apiResponse.data?.anatomy_category || template.anatomy_category,
      anatomy_id: apiResponse.data?.anatomy_id || template.anatomy_id,
      facility_id: apiResponse.data?.facility_id || template.facility_id,
      access_level: apiResponse.data?.access_level || template.access_level || 'Private',
      status: apiResponse.data?.status || template.status || 'Active',
      version: apiResponse.data?.version || 1,
      createdAt: apiResponse.data?.createdAt || new Date().toISOString(),
      updatedAt: apiResponse.data?.updatedAt || new Date().toISOString(),
    };

    this._broadcastEvent(TemplateService.EVENTS.TEMPLATE_CREATED, newTemplate);
    return newTemplate;
  }

  /**
   * Update an existing template
   * @param options.useMasterTemplates — PUT `/ris/api/master-templates/:id` (ris-super-admin); requires `modalityId`.
   */
  public async updateTemplate(
    id: string,
    updates: Partial<Omit<Template, 'id'>>,
    options?: { useMasterTemplates?: boolean; modalityId?: number }
  ): Promise<Template | null> {
    const apiConfig = getApiConfig();

    const useMaster =
      options?.useMasterTemplates === true &&
      options.modalityId != null &&
      !Number.isNaN(options.modalityId);

    if (useMaster) {
      const anatomyId = updates.anatomy_id;

      const requestBody = {
        modality_id: options!.modalityId as number,
        anatomy_id: anatomyId ?? null,
        view: (updates.viewLabel ?? '').trim(),
        diagnosis: (updates.diagnosisLabel ?? '').trim(),
        template_content_findings: updates.findings ?? '',
        template_content_impression: updates.impression ?? '',
        is_imported: Boolean(updates.isDocumentImport),
        is_deleted: Boolean(updates.is_deleted),
        ...(updates.name != null && String(updates.name).trim() !== ''
          ? { template_name: String(updates.name).trim() }
          : {}),
      };

      const response = await fetch(apiConfig.MASTER_TEMPLATES.UPDATE(id), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      const apiResponse = parseRisJsonBody(await response.text());
      assertRisSuccess(response, apiResponse);
      const row = apiResponse.data as Record<string, any> | undefined;

      const responseTemplate: Template = {
        id: row?.id?.toString() || id,
        name: row?.template_name || updates.name || '',
        modality: (row?.Modality?.name || updates.modality) as Template['modality'],
        findings: row?.template_content_findings ?? updates.findings ?? '',
        impression: row?.template_content_impression ?? updates.impression ?? '',
        isDocumentImport: Boolean(row?.is_imported ?? updates.isDocumentImport),
        anatomy_category: row?.AnatomyPart?.name || updates.anatomy_category,
        anatomy_id: row?.anatomy_id ?? updates.anatomy_id,
        facility_id: updates.facility_id,
        access_level: updates.access_level || 'Private',
        status: row?.is_deleted === true || updates.status === 'Inactive' ? 'Inactive' : 'Active',
        version: row?.version ?? updates.version,
        is_deleted: Boolean(row?.is_deleted ?? updates.is_deleted),
        createdAt: row?.createdAt || updates.createdAt || new Date().toISOString(),
        updatedAt: row?.updatedAt || new Date().toISOString(),
        viewLabel: row?.view ?? updates.viewLabel,
        diagnosisLabel: row?.diagnosis ?? updates.diagnosisLabel,
        updatedByName:
          row?.LastModifier?.full_name ?? row?.Creator?.full_name ?? updates.updatedByName,
      };

      this._broadcastEvent(TemplateService.EVENTS.TEMPLATE_UPDATED, responseTemplate);
      return responseTemplate;
    }

    // For API-only approach, we don't need to get current template from localStorage
    // Just use the updates directly
    const updatedTemplate = {
      id,
      ...updates,
      updatedAt: new Date().toISOString(),
    } as Template;

    const tenantCatalogPut =
      options?.modalityId != null && !Number.isNaN(Number(options.modalityId));

    if (tenantCatalogPut) {
      const anatomyId = updatedTemplate.anatomy_id;
      if (anatomyId == null) {
        throw new Error('Body part is required');
      }

      const tcBody: Record<string, unknown> = {
        modality_id: options!.modalityId as number,
        anatomy_id: anatomyId,
        view: (updatedTemplate.viewLabel ?? '').trim(),
        diagnosis: (updatedTemplate.diagnosisLabel ?? '').trim(),
        is_imported: Boolean(updatedTemplate.isDocumentImport),
        template_content_findings: updatedTemplate.findings || '',
      };

      const impTrimPut = (updatedTemplate.impression || '').trim();
      if (!(updatedTemplate.isDocumentImport && !impTrimPut)) {
        tcBody.template_content_impression = updatedTemplate.impression || '';
      }

      if (updatedTemplate.access_level) {
        tcBody.access_level = updatedTemplate.access_level;
      }
      if (updatedTemplate.status) {
        tcBody.status = updatedTemplate.status;
      }
      if (updatedTemplate.facility_id) {
        tcBody.facility_id = updatedTemplate.facility_id;
      }
      if (updatedTemplate.is_deleted !== undefined) {
        tcBody.is_deleted = updatedTemplate.is_deleted;
      }
      if (updates.template_description !== undefined) {
        tcBody.template_description = String(updates.template_description ?? '').trim();
      }

      const tcResponse = await fetch(apiConfig.TEMPLATES.UPDATE(id), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(tcBody),
      });

      const tcApiResponse = parseRisJsonBody(await tcResponse.text());
      assertRisSuccess(tcResponse, tcApiResponse);

      const tcRow = tcApiResponse.data as Record<string, any> | undefined;
      const modalityFromNest = tcRow?.Modality?.name as string | undefined;
      const responseTemplateTc: Template = {
        id: tcRow?.id?.toString() || id,
        name: (tcRow?.template_name as string) || updatedTemplate.name,
        modality: (modalityFromNest ||
          tcRow?.modality ||
          updatedTemplate.modality) as Template['modality'],
        findings: (tcRow?.template_content_findings as string) || updatedTemplate.findings,
        impression: (tcRow?.template_content_impression as string) || updatedTemplate.impression,
        isDocumentImport: Boolean(tcRow?.is_imported ?? updatedTemplate.isDocumentImport),
        anatomy_category: (tcRow?.anatomy_category as string) || updatedTemplate.anatomy_category,
        anatomy_id: (tcRow?.anatomy_id as number) ?? updatedTemplate.anatomy_id,
        facility_id: (tcRow?.facility_id ?? updatedTemplate.facility_id) as Template['facility_id'],
        access_level:
          (tcRow?.access_level as Template['access_level']) || updatedTemplate.access_level,
        status: (tcRow?.status as Template['status']) || updatedTemplate.status,
        version: (tcRow?.version as number) ?? updatedTemplate.version,
        is_deleted: Boolean(tcRow?.is_deleted ?? updatedTemplate.is_deleted ?? false),
        createdAt: (tcRow?.createdAt as string) || updatedTemplate.createdAt,
        updatedAt: (tcRow?.updatedAt as string) || new Date().toISOString(),
        viewLabel: (tcRow?.view as string) ?? updatedTemplate.viewLabel,
        diagnosisLabel: (tcRow?.diagnosis as string) ?? updatedTemplate.diagnosisLabel,
      };

      this._broadcastEvent(TemplateService.EVENTS.TEMPLATE_UPDATED, responseTemplateTc);
      return responseTemplateTc;
    }

    // Prepare the request body according to API specification
    const requestBody: any = {
      template_name: updatedTemplate.name,
      modality: updatedTemplate.modality,
      anatomy_category: updatedTemplate.anatomy_category || '',
      is_imported: updatedTemplate.isDocumentImport || false, // Set to true if template was imported from a document
      template_content_findings: updatedTemplate.findings || '',
      template_content_impression: updatedTemplate.impression || '',
      access_level: updatedTemplate.access_level || 'Private',
      status: updatedTemplate.status || 'Active', // Default to Active
    };

    // Add anatomy_id if provided
    if (updatedTemplate.anatomy_id) {
      requestBody.anatomy_id = updatedTemplate.anatomy_id;
    }

    // Add facility_id if provided (for ris-super-admin)
    if (updatedTemplate.facility_id) {
      requestBody.facility_id = updatedTemplate.facility_id;
    }

    // Add is_deleted if provided
    if (updatedTemplate.is_deleted !== undefined) {
      requestBody.is_deleted = updatedTemplate.is_deleted;
    }

    if (updatedTemplate.viewLabel !== undefined) {
      requestBody.view = updatedTemplate.viewLabel;
    }
    if (updatedTemplate.diagnosisLabel !== undefined) {
      requestBody.diagnosis = updatedTemplate.diagnosisLabel;
    }

    // Update via API (no localStorage fallback for updating)
    const response = await fetch(apiConfig.TEMPLATES.UPDATE(id), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody),
    });

    const apiResponse = parseRisJsonBody(await response.text());
    assertRisSuccess(response, apiResponse);

    // Map API response to Template object
    const responseTemplate: Template = {
      id: apiResponse.data?.id?.toString() || id,
      name: apiResponse.data?.template_name || updatedTemplate.name,
      // Extract modality from Modality.name if Modality object exists, otherwise fallback to updatedTemplate.modality
      modality: (apiResponse.data?.Modality?.name ||
        apiResponse.data?.modality ||
        updatedTemplate.modality) as 'MR' | 'CT' | 'XR' | 'US',
      findings: apiResponse.data?.template_content_findings || updatedTemplate.findings,
      impression: apiResponse.data?.template_content_impression || updatedTemplate.impression,
      isDocumentImport: apiResponse.data?.is_imported || updatedTemplate.isDocumentImport,
      anatomy_category: apiResponse.data?.anatomy_category || updatedTemplate.anatomy_category,
      anatomy_id: apiResponse.data?.anatomy_id || updatedTemplate.anatomy_id,
      facility_id: apiResponse.data?.facility_id || updatedTemplate.facility_id,
      access_level: apiResponse.data?.access_level || updatedTemplate.access_level,
      status: apiResponse.data?.status || updatedTemplate.status,
      version: apiResponse.data?.version || updatedTemplate.version,
      is_deleted: apiResponse.data?.is_deleted ?? updatedTemplate.is_deleted ?? false,
      createdAt: apiResponse.data?.createdAt || updatedTemplate.createdAt,
      updatedAt: apiResponse.data?.updatedAt || new Date().toISOString(),
      viewLabel: apiResponse.data?.view ?? updatedTemplate.viewLabel,
      diagnosisLabel: apiResponse.data?.diagnosis ?? updatedTemplate.diagnosisLabel,
    };

    this._broadcastEvent(TemplateService.EVENTS.TEMPLATE_UPDATED, responseTemplate);
    return responseTemplate;
  }

  /**
   * Master catalog (ris-super-admin): toggle active/inactive via PUT with `{ is_deleted }` only.
   * Active → inactive: `is_deleted: true`; inactive → active: `is_deleted: false`.
   */
  public async updateMasterTemplateDeletedFlag(id: string, isDeleted: boolean): Promise<void> {
    const apiConfig = getApiConfig();
    const response = await fetch(apiConfig.MASTER_TEMPLATES.UPDATE(id), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_deleted: isDeleted }),
    });

    const body = parseRisJsonBody(await response.text());
    assertRisSuccess(response, body);
  }

  /**
   * Tenant: set template(s) active or inactive (`POST /ris/api/templates/status`).
   * Use either `template_id` (one) or `template_ids` (many), not both.
   */
  public async postTemplatesStatus(
    payload:
      | { status: TemplateRisStatus; template_id: number }
      | { status: TemplateRisStatus; template_ids: number[] }
  ): Promise<void> {
    const apiConfig = getApiConfig();

    let bodyJson: { status: TemplateRisStatus; template_id: number } | {
      status: TemplateRisStatus;
      template_ids: number[];
    };

    if ('template_id' in payload) {
      if (!Number.isFinite(payload.template_id) || payload.template_id <= 0) {
        throw new Error('Invalid template_id');
      }
      bodyJson = { status: payload.status, template_id: payload.template_id };
    } else {
      const ids = [...new Set(payload.template_ids.filter(id => Number.isFinite(id) && id > 0))];
      if (ids.length === 0) {
        throw new Error('No valid template ids');
      }
      bodyJson = { status: payload.status, template_ids: ids };
    }

    const response = await fetch(apiConfig.TEMPLATES.STATUS, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(bodyJson),
    });

    const body = parseRisJsonBody(await response.text());
    assertRisSuccess(response, body);
  }

  /**
   * Fetch templates from API with pagination and filtering
   */
  public async fetchTemplatesFromApi(options?: {
    page?: number;
    limit?: number;
    modality?: string;
    /** Master-templates list expects numeric modality_id (maps from Modality.name → id on the client). */
    modality_id?: number;
    anatomy_id?: number;
    facility_id?: number;
    /** Master & tenant list: server-side `search` query (e.g. template name / content). */
    search?: string;
    /** Master-templates list: `active` | `inactive` (lowercase query). Tenant GET `/ris/api/templates`: `ACTIVE` | `INACTIVE` (accept `active`/`inactive` or uppercase — normalized per route). */
    status?: 'active' | 'inactive' | 'ACTIVE' | 'INACTIVE';
    /** Tenant list only: `GET /ris/api/templates?type=SYSTEM` or `type=CUSTOM` */
    templatesListType?: 'SYSTEM' | 'CUSTOM';
    /** When set (e.g. super-admin master catalog), list GET uses this URL instead of TEMPLATES.GET */
    templatesListBaseUrl?: string;
  }): Promise<{
    templates: Template[];
    total: number;
    page: number;
    totalPages: number;
    message?: string;
    /** Tenant list: `total_update_available` from API when present */
    totalUpdateAvailable?: number;
    /** Tenant list: explicit ids with catalogue updates (`update_available_system_template_ids`) */
    updateAvailableSystemTemplateIds?: number[];
  }> {
    const apiConfig = getApiConfig();

    const listBase = options?.templatesListBaseUrl || apiConfig.TEMPLATES.GET;
    const isMasterList = listBase.includes('master-templates');

    // Build query parameters
    const params = new URLSearchParams();
    params.append('page', (options?.page || 1).toString());
    params.append('limit', (options?.limit || 100).toString()); // Default to 100 to get all templates

    if (isMasterList) {
      if (options?.modality_id != null && !Number.isNaN(options.modality_id)) {
        params.append('modality_id', String(options.modality_id));
      }
      const q = options?.search?.trim();
      if (q) {
        params.append('search', q);
      }
      if (options?.status != null) {
        const masterStatus = String(options.status).toLowerCase();
        if (masterStatus === 'active' || masterStatus === 'inactive') {
          params.append('status', masterStatus);
        }
      }
      if (options?.anatomy_id) {
        params.append('anatomy_id', options.anatomy_id.toString());
      }
      // Master list does not use facility_id in the catalog query
    } else {
      if (options?.modality) {
        params.append('modality', options.modality);
      }
      if (options?.anatomy_id) {
        params.append('anatomy_id', options.anatomy_id.toString());
      }
      if (options?.facility_id) {
        params.append('facility_id', options.facility_id.toString());
      }
      /** Tenant `/ris/api/templates`: backend expects query `ACTIVE` | `INACTIVE`. */
      if (options?.status != null) {
        const tenantStatus = String(options.status).toUpperCase();
        if (tenantStatus === 'ACTIVE' || tenantStatus === 'INACTIVE') {
          params.append('status', tenantStatus);
        }
      }
      if (options?.templatesListType === 'SYSTEM' || options?.templatesListType === 'CUSTOM') {
        params.append('type', options.templatesListType);
      }
      const tenantSearch = options?.search?.trim();
      if (tenantSearch) {
        params.append('search', tenantSearch);
      }
    }

    const url = `${listBase}?${params.toString()}`;

    // Fetch from API only - no localStorage caching or fallback
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = parseRisJsonBody(await response.text());
      throw new Error(
        errorData.error || errorData.message || `API request failed: ${response.status}`
      );
    }

    const apiResponse = parseRisJsonBody(await response.text());
    assertRisSuccess(response, apiResponse);

    // Map API response to Template format
    const apiTemplates: Template[] = (apiResponse.data || []).map((item: any) =>
      this._mapApiTemplateRow(item)
    );

    const pagination = apiResponse.pagination as
      | {
          totalItems?: number;
          /** Tenant list uses `totalTemplates` in some API versions */
          totalTemplates?: number;
          currentPage?: number;
          totalPages?: number;
        }
      | undefined;

    const envExtra = apiResponse as RisEnvelope & {
      total_update_available?: unknown;
      update_available_system_template_ids?: unknown;
    };
    const totalUpdateRaw = envExtra.total_update_available;
    const totalUpdateAvailable =
      typeof totalUpdateRaw === 'number' && Number.isFinite(totalUpdateRaw)
        ? totalUpdateRaw
        : undefined;

    let updateAvailableSystemTemplateIds: number[] | undefined;
    const rawIdList = envExtra.update_available_system_template_ids;
    if (Array.isArray(rawIdList)) {
      updateAvailableSystemTemplateIds = rawIdList
        .map(id => (typeof id === 'number' ? id : Number(id)))
        .filter(id => Number.isFinite(id) && id > 0);
    }

    return {
      templates: apiTemplates,
      total:
        pagination?.totalItems ??
        pagination?.totalTemplates ??
        apiResponse.total ??
        apiTemplates.length,
      page: pagination?.currentPage ?? apiResponse.page ?? options?.page ?? 1,
      totalPages: pagination?.totalPages ?? apiResponse.totalPages ?? 1,
      message: apiResponse.message,
      totalUpdateAvailable,
      updateAvailableSystemTemplateIds,
    };
  }

  /**
   * Tenant: facility template vs catalogue master (`GET …/templates/:id/with-master`).
   */
  public async fetchTemplateWithMaster(id: string): Promise<{
    current: Template;
    master: Template | null;
  }> {
    const apiConfig = getApiConfig();
    const response = await fetch(apiConfig.TEMPLATES.WITH_MASTER(id), {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    const body = parseRisJsonBody(await response.text());
    assertRisSuccess(response, body);

    const data = body.data as
      | { current_template?: Record<string, unknown>; master_template?: Record<string, unknown> | null }
      | undefined;
    const rawCurrent = data?.current_template;
    if (!rawCurrent || typeof rawCurrent !== 'object') {
      throw new Error(body.message || 'Invalid response: missing current_template');
    }

    const current = this._mapApiTemplateRow(rawCurrent as Record<string, any>);
    const rawMaster = data?.master_template;
    const master =
      rawMaster != null && typeof rawMaster === 'object'
        ? this._mapApiTemplateRow(rawMaster as Record<string, any>)
        : null;

    return { current, master };
  }

  /**
   * Tenant: system template update vs keep current (`POST …/templates/master-action`).
   * Pass one or more facility template ids; same payload supports bulk.
   */
  public async postTemplatesMasterAction(
    templateIds: number[],
    action: TemplateMasterActionType
  ): Promise<{
    message?: string;
    action: TemplateMasterActionType;
    results: TemplateMasterActionResultItem[];
  }> {
    const apiConfig = getApiConfig();
    const ids = [...new Set(templateIds)].filter(id => Number.isFinite(id) && id > 0);
    if (ids.length === 0) {
      throw new Error('No valid template ids');
    }

    const response = await fetch(apiConfig.TEMPLATES.MASTER_ACTION, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ template_ids: ids, action }),
    });

    const body = parseRisJsonBody(await response.text());
    assertRisSuccess(response, body);

    const data = body.data as
      | { action?: string; results?: TemplateMasterActionResultItem[] }
      | undefined;

    return {
      message: typeof body.message === 'string' ? body.message : undefined,
      action: (data?.action as TemplateMasterActionType) || action,
      results: Array.isArray(data?.results) ? data!.results! : [],
    };
  }

  /**
   * GET master template catalog for ris-radiologist “Import from library”.
   */
  public async fetchMasterTemplateCatalog(): Promise<MasterCatalogItem[]> {
    const apiConfig = getApiConfig();
    const response = await fetch(apiConfig.TEMPLATES.MASTER_CATALOG, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    const body = parseRisJsonBody(await response.text());
    assertRisSuccess(response, body);
    const raw = body.data;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item: Record<string, unknown>) => ({
      id: Number(item.id),
      template_name: String(item.template_name ?? ''),
      version: Number(item.version ?? 1),
      imported: Boolean(item.imported),
    }));
  }

  /**
   * Import master-catalog templates into the current tenant (batch).
   * Backend: POST `/ris/api/templates` with `{ master_template_ids: number[] }`.
   */
  public async importTemplatesFromMasterCatalog(ids: number[]): Promise<void> {
    const apiConfig = getApiConfig();
    const uniqueIds = [...new Set(ids)].filter(id => Number.isFinite(id) && id > 0);
    if (uniqueIds.length === 0) {
      return;
    }
    const response = await fetch(apiConfig.TEMPLATES.IMPORT_MASTER_CATALOG, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ master_template_ids: uniqueIds }),
    });
    const parsed = parseRisJsonBody(await response.text());
    assertRisSuccess(response, parsed);
  }

  /**
   * Import templates from JSON
   */
  public async importTemplates(
    templatesData: Template[],
    facilityId?: number
  ): Promise<{ success: number; errors: number }> {
    let success = 0;
    let errors = 0;

    // Process templates sequentially to avoid overwhelming the API
    for (const template of templatesData) {
      try {
        // Validate template structure
        if (this._validateTemplate(template)) {
          await this.createTemplate({
            name: template.name,
            modality: template.modality,
            findings: template.findings,
            impression: template.impression,
            isDocumentImport: template.isDocumentImport,
            anatomy_category: template.anatomy_category,
            access_level: template.access_level,
            facility_id: facilityId || template.facility_id,
          });
          success++;
        } else {
          errors++;
        }
      } catch (error) {
        console.error('Error importing template:', error);
        errors++;
      }
    }

    return { success, errors };
  }

  /**
   * Export templates to JSON
   */
  public exportTemplates(templateIds?: string[]): string {
    let templates = this.getAllTemplates();

    if (templateIds && templateIds.length > 0) {
      templates = templates.filter(t => templateIds.includes(t.id));
    }

    return JSON.stringify(templates, null, 2);
  }

  /**
   * Private helper to save templates to localStorage
   */
  private _saveTemplates(templates: Template[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    } catch (error) {
      console.error('Error saving templates to localStorage:', error);
      throw new Error('Failed to save templates');
    }
  }

  /**
   * Private helper to generate unique IDs
   */
  private _generateId(): string {
    return `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Private helper to validate template structure
   */
  private _validateTemplate(template: Partial<Template>): boolean {
    return (
      template &&
      typeof template.name === 'string' &&
      ['MR', 'CT', 'XR', 'US'].includes(template.modality) &&
      typeof template.findings === 'string' &&
      typeof template.impression === 'string'
    );
  }
}

export default TemplateService;
